import { useMemo } from 'react';
import { Flame, Trophy, TrendingUp, TrendingDown, Calendar, Crown } from 'lucide-react';
import { GameHistoryItem } from '../modules/stats/types';
import { ROLE_NAMES, PlayerRole } from '../types/stats';

interface WinStreakTrackerProps {
  games: GameHistoryItem[];
  playerNumber: number;
  currentTargetPlayerName?: string;
}

interface StreakInfo {
  currentStreak: number;
  currentType: 'win' | 'loss' | null;
  longestWinStreak: number;
  longestLossStreak: number;
  longestWinPeriod: { start: string; end: string } | null;
  longestLossPeriod: { start: string; end: string } | null;
  gameHistory: {
    gameId: number;
    date: string;
    gameNumber: number;
    result: 'win' | 'loss';
    role: string;
    scoreChange: number;
    isMvp: boolean;
    victoryType: string;
  }[];
}

export default function WinStreakTracker({ 
  games, 
  playerNumber,
  currentTargetPlayerName,
}: WinStreakTrackerProps) {
  const streakInfo = useMemo<StreakInfo>(() => {
    // V138: 确定当前目标玩家昵称
    const targetPlayerName = currentTargetPlayerName || 
      games.flatMap(g => g.players || [])
        .find(p => p.player_number === playerNumber)?.player_nickname;

    if (!targetPlayerName) {
      console.log('[V138] 连胜追踪 - 未找到目标玩家昵称:', { playerNumber });
      return {
        currentStreak: 0,
        currentType: null,
        longestWinStreak: 0,
        longestLossStreak: 0,
        longestWinPeriod: null,
        longestLossPeriod: null,
        gameHistory: [],
      };
    }

    // V138: 按日期 + 局数排序的游戏记录
    const sortedGames = [...games]
      .filter(g => g.players?.some(p => p.player_nickname === targetPlayerName))
      .sort((a, b) => {
        const dateA = new Date(a.game_date || '').getTime();
        const dateB = new Date(b.game_date || '').getTime();
        if (dateA !== dateB) return dateA - dateB;
        return (a.game_number || 0) - (b.game_number || 0);
      });

    console.log('[V138] 连胜追踪 - 排序后的游戏:', sortedGames.map(g => ({
      date: g.game_date,
      gameNumber: g.game_number,
    })));

    // V138: 构建连续关系链
    const consecutiveChains: Array<{ games: typeof sortedGames }> = [];
    let currentChain: typeof sortedGames = [];

    for (let i = 0; i < sortedGames.length; i++) {
      const current = sortedGames[i];
      const next = sortedGames[i + 1];

      currentChain.push(current);

      if (next) {
        const isConsecutive = checkIsConsecutive(current, next, sortedGames);
        if (!isConsecutive) {
          consecutiveChains.push({ games: [...currentChain] });
          currentChain = [];
        }
      } else {
        // 最后一个链
        consecutiveChains.push({ games: [...currentChain] });
      }
    }

    console.log('[V138] 连胜追踪 - 连续链数量:', consecutiveChains.length);

    // V138/V268: 在每个连续链中计算连胜/连败
    const gameHistory: { gameId: number; date: string; gameNumber: number; result: 'win' | 'loss'; role: string; scoreChange: number; isMvp: boolean; victoryType: string }[] = [];
    let longestWinStreak = 0;
    let longestLossStreak = 0;
    let longestWinPeriod: { start: string; end: string } | null = null;
    let longestLossPeriod: { start: string; end: string } | null = null;

    consecutiveChains.forEach(chain => {
      let tempWinStreak = 0;
      let tempLossStreak = 0;
      let currentWinStart: string | null = null;
      let currentLossStart: string | null = null;

      chain.games.forEach(game => {
        const player = game.players?.find(p => p.player_nickname === targetPlayerName);
        if (!player) return;

        const isWin = player.faction === game.winning_faction;
        const result: 'win' | 'loss' = isWin ? 'win' : 'loss';

        gameHistory.push({
          gameId: game.id,
          date: game.game_date || '',
          gameNumber: game.game_number || 0,
          result,
          role: player.player_role || '',
          scoreChange: player.score_change || 0,
          isMvp: player.is_mvp || false,
          victoryType: game.victory_type || '',
        });

        if (isWin) {
          if (tempWinStreak === 0) {
            currentWinStart = game.game_date || '';
          }
          tempWinStreak++;
          tempLossStreak = 0;
          if (tempWinStreak > longestWinStreak) {
            longestWinStreak = tempWinStreak;
            longestWinPeriod = {
              start: currentWinStart || '',
              end: game.game_date || '',
            };
          }
        } else {
          if (tempLossStreak === 0) {
            currentLossStart = game.game_date || '';
          }
          tempLossStreak++;
          tempWinStreak = 0;
          if (tempLossStreak > longestLossStreak) {
            longestLossStreak = tempLossStreak;
            longestLossPeriod = {
              start: currentLossStart || '',
              end: game.game_date || '',
            };
          }
        }
      });
    });

    // V138: 计算当前连胜/连败（从最后一个连续链的最后一个游戏开始）
    let currentStreak = 0;
    let currentType: 'win' | 'loss' | null = null;

    if (gameHistory.length > 0) {
      const lastChain = consecutiveChains[consecutiveChains.length - 1];
      if (lastChain && lastChain.games.length > 0) {
        const lastGame = lastChain.games[lastChain.games.length - 1];
        const lastPlayer = lastGame.players?.find(p => p.player_nickname === targetPlayerName);
        if (lastPlayer) {
          currentType = lastPlayer.faction === lastGame.winning_faction ? 'win' : 'loss';
          
          // 从后往前计算当前连胜/连败
          currentStreak = 0;
          for (let i = lastChain.games.length - 1; i >= 0; i--) {
            const game = lastChain.games[i];
            const player = game.players?.find(p => p.player_nickname === targetPlayerName);
            if (!player) continue;
            
            const isWin = player.faction === game.winning_faction;
            const result: 'win' | 'loss' = isWin ? 'win' : 'loss';
            
            if (result === currentType) {
              currentStreak++;
            } else {
              break;
            }
          }
        }
      }
    }

    console.log('[V138] 连胜追踪 - 统计结果:', {
      currentStreak,
      currentType,
      longestWinStreak,
      longestLossStreak,
      totalGames: gameHistory.length,
    });

    return {
      currentStreak,
      currentType,
      longestWinStreak,
      longestLossStreak,
      longestWinPeriod,
      longestLossPeriod,
      gameHistory,
    };
  }, [games, playerNumber, currentTargetPlayerName]);

  // V138: 判断两个游戏是否构成连续关系
  function checkIsConsecutive(
    gameA: GameHistoryItem, 
    gameB: GameHistoryItem, 
    allGames: GameHistoryItem[]
  ): boolean {
    const dateA = new Date(gameA.game_date || '');
    const dateB = new Date(gameB.game_date || '');
    const gameNumA = gameA.game_number || 0;
    const gameNumB = gameB.game_number || 0;

    // V138: 情况 1：同一天，局数连续（如第 1 局→第 2 局）
    if (dateA.toDateString() === dateB.toDateString()) {
      return gameNumB === gameNumA + 1;
    }

    // V138: 情况 2：跨天连续（A 是当天最后一局，B 是之后最近一天的第一局）
    const gamesOnDateA = allGames.filter(g => 
      new Date(g.game_date || '').toDateString() === dateA.toDateString()
    );
    const isLastGameOfDateA = gameA === gamesOnDateA[gamesOnDateA.length - 1];

    const gamesAfterDateA = allGames.filter(g => 
      new Date(g.game_date || '') > dateA
    ).sort((a, b) => 
      new Date(a.game_date || '').getTime() - new Date(b.game_date || '').getTime()
    );
    
    if (gamesAfterDateA.length === 0) return false;
    
    const isFirstGameAfterDateA = gameB === gamesAfterDateA[0];

    return isLastGameOfDateA && isFirstGameAfterDateA;
  }

  const getStreakText = () => {
    if (streakInfo.currentStreak === 0) return '暂无记录';
    if (streakInfo.currentType === 'win') {
      return `${streakInfo.currentStreak} 连胜`;
    }
    return `${streakInfo.currentStreak} 连败`;
  };

  const getStreakColor = () => {
    if (streakInfo.currentType === 'win') return 'text-green-400';
    if (streakInfo.currentType === 'loss') return 'text-red-400';
    return 'text-avalon-text';
  };

  const getStreakBg = () => {
    if (streakInfo.currentType === 'win') return 'bg-green-500/20 border-green-500/30';
    if (streakInfo.currentType === 'loss') return 'bg-red-500/20 border-red-500/30';
    return 'bg-avalon-card border-avalon-purple/20';
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {/* 当前状态卡片 */}
      <div className={`p-4 rounded-xl border ${getStreakBg()}`}>
        <div className="flex items-center gap-3">
          {streakInfo.currentType === 'win' ? (
            <div className="w-12 h-12 rounded-full bg-green-500/30 flex items-center justify-center">
              <Flame className="w-6 h-6 text-green-400" />
            </div>
          ) : streakInfo.currentType === 'loss' ? (
            <div className="w-12 h-12 rounded-full bg-red-500/30 flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-400" />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-full bg-avalon-card flex items-center justify-center">
              <Trophy className="w-6 h-6 text-avalon-gold" />
            </div>
          )}
          <div>
            <div className="text-avalon-text text-sm">当前状态</div>
            <div className={`text-2xl font-bold ${getStreakColor()}`}>
              {getStreakText()}
            </div>
          </div>
        </div>
      </div>

      {/* V1.2: 历史记录 - 连胜连败卡片并排显示 */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-2 bg-avalon-card rounded-lg border border-avalon-purple/20">
          <div className="flex items-center gap-1 mb-1">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-avalon-text text-xs">最长连胜</span>
          </div>
          <div className="text-xl font-bold text-green-400 mb-1">
            {streakInfo.longestWinStreak} 场
          </div>
          {streakInfo.longestWinPeriod && streakInfo.longestWinStreak > 0 && (
            <div className="flex items-center gap-1 text-xs text-avalon-text">
              <Calendar className="w-3 h-3" />
              <span className="text-xs">
                {formatDate(streakInfo.longestWinPeriod.start)} - {formatDate(streakInfo.longestWinPeriod.end)}
              </span>
            </div>
          )}
        </div>
        <div className="p-2 bg-avalon-card rounded-lg border border-avalon-purple/20">
          <div className="flex items-center gap-1 mb-1">
            <TrendingDown className="w-4 h-4 text-red-400" />
            <span className="text-avalon-text text-xs">最长连败</span>
          </div>
          <div className="text-xl font-bold text-red-400 mb-1">
            {streakInfo.longestLossStreak} 场
          </div>
          {streakInfo.longestLossPeriod && streakInfo.longestLossStreak > 0 && (
            <div className="flex items-center gap-1 text-xs text-avalon-text">
              <Calendar className="w-3 h-3" />
              <span className="text-xs">
                {formatDate(streakInfo.longestLossPeriod.start)} - {formatDate(streakInfo.longestLossPeriod.end)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 时间轴 */}
      {streakInfo.gameHistory.length > 0 && (
        <div className="mt-4">
          <h4 className="text-white font-medium mb-3">胜负时间轴</h4>
          <div className="relative">
            {/* 时间线 */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-avalon-purple/30" />

            <div className="space-y-3 max-h-64 overflow-y-auto">
              {streakInfo.gameHistory.slice().reverse().map((game) => (
                <div key={game.gameId} className="flex items-center gap-3 pl-1">
                  {/* 节点 */}
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold z-10 ${
                      game.result === 'win'
                        ? 'bg-green-500 text-white'
                        : 'bg-red-500 text-white'
                    }`}
                  >
                    {game.result === 'win' ? 'W' : 'L'}
                  </div>

                  {/* 信息 */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm">
                          {game.result === 'win' ? '胜利' : '失败'}
                        </span>
                        {/* V154: 角色名本地化 - 显示中文标准名 */}
                        {game.role && (
                          <span className="text-xs text-avalon-text bg-avalon-bg-dark px-1.5 py-0.5 rounded">
                            {ROLE_NAMES[game.role as PlayerRole] || game.role}
                          </span>
                        )}
                        {/* MVP 皇冠图标 */}
                        {game.isMvp && (
                          <Crown className="w-3.5 h-3.5 text-yellow-400" />
                        )}
                        {/* 获胜方式 - 金色文字 */}
                        {game.victoryType && (
                          <span className="text-xs text-amber-400 font-medium">
                            {game.victoryType === 'mission' ? '完成任务梅林隐藏' : 
                             game.victoryType === 'assassin_success' ? '刺杀梅林成功' :
                             game.victoryType === 'sabotage' ? '破坏三次任务' :
                             game.victoryType === 'assassin_fail' ? '提前刺杀失败' :
                             game.victoryType === 'wrong_kill' ? '误杀' :
                             game.victoryType === 'bug' ? 'BUG 结算' :
                             game.victoryType}
                          </span>
                        )}
                      </div>
                      {/* V268: 积分变动 */}
                      <span className={`text-sm font-bold ${game.scoreChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {game.scoreChange >= 0 ? '+' : ''}{game.scoreChange}
                      </span>
                    </div>
                    <div className="text-xs text-avalon-text">
                      {new Date(game.date).toLocaleDateString('zh-CN')} · 第{game.gameNumber}局
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
